import React from 'react';
/**
 * Adapter that wraps a MUI SvgIcon component to match Jitsi's icon interface.
 * Jitsi's Icon component passes: fill, height, width, id, aria-label, aria-hidden, role
 */
function adapt(MuiIcon) {
    const AdaptedIcon = (props) => {
        const { fill, height, width, ...rest } = props;
        return (React.createElement(MuiIcon, { style: {
                color: fill || undefined,
                width: width ?? undefined,
                height: height ?? undefined,
                fontSize: typeof height === 'number' ? height : undefined
            }, ...rest }));
    };
    AdaptedIcon.displayName = `Adapted(${MuiIcon.displayName || MuiIcon.name || 'MuiIcon'})`;
    return AdaptedIcon;
}
// MUI icon imports
import AccountCircle from '@mui/icons-material/AccountCircle';
import Add from '@mui/icons-material/Add';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import Bluetooth from '@mui/icons-material/Bluetooth';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import CallEnd from '@mui/icons-material/CallEnd';
import Cameraswitch from '@mui/icons-material/Cameraswitch';
import Cancel from '@mui/icons-material/Cancel';
import Chat from '@mui/icons-material/Chat';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Close from '@mui/icons-material/Close';
import CloudUpload from '@mui/icons-material/CloudUpload';
import Code from '@mui/icons-material/Code';
import ContactPage from '@mui/icons-material/ContactPage';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Delete from '@mui/icons-material/Delete';
import Description from '@mui/icons-material/Description';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import Download from '@mui/icons-material/Download';
import Draw from '@mui/icons-material/Draw';
import Edit from '@mui/icons-material/Edit';
import Email from '@mui/icons-material/Email';
import EmojiEmotions from '@mui/icons-material/EmojiEmotions';
import EnhancedEncryption from '@mui/icons-material/EnhancedEncryption';
import Error from '@mui/icons-material/Error';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorder from '@mui/icons-material/FavoriteBorder';
import Feedback from '@mui/icons-material/Feedback';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import GridView from '@mui/icons-material/GridView';
import Headphones from '@mui/icons-material/Headphones';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Highlight from '@mui/icons-material/Highlight';
import Image from '@mui/icons-material/Image';
import Info from '@mui/icons-material/Info';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Keyboard from '@mui/icons-material/Keyboard';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import Language from '@mui/icons-material/Language';
import Lock from '@mui/icons-material/Lock';
import MarkChatUnread from '@mui/icons-material/MarkChatUnread';
import Mic from '@mui/icons-material/Mic';
import MicOff from '@mui/icons-material/MicOff';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import NoEncryption from '@mui/icons-material/NoEncryption';
import Notifications from '@mui/icons-material/Notifications';
import OpenInFull from '@mui/icons-material/OpenInFull';
import PanToolAlt from '@mui/icons-material/PanToolAlt';
import People from '@mui/icons-material/People';
import Person from '@mui/icons-material/Person';
import PersonAdd from '@mui/icons-material/PersonAdd';
import PersonOff from '@mui/icons-material/PersonOff';
import PhoneInTalk from '@mui/icons-material/PhoneInTalk';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PushPin from '@mui/icons-material/PushPin';
import PushPinOutlined from '@mui/icons-material/PushPinOutlined';
import Reply from '@mui/icons-material/Reply';
import Restore from '@mui/icons-material/Restore';
import ScreenShare from '@mui/icons-material/ScreenShare';
import Search from '@mui/icons-material/Search';
import Send from '@mui/icons-material/Send';
import Settings from '@mui/icons-material/Settings';
import Share from '@mui/icons-material/Share';
import Shield from '@mui/icons-material/Shield';
import SignalCellularAlt from '@mui/icons-material/SignalCellularAlt';
import SignalCellularConnectedNoInternet0Bar from '@mui/icons-material/SignalCellularConnectedNoInternet0Bar';
import SignalWifi1Bar from '@mui/icons-material/SignalWifi1Bar';
import SignalWifi2Bar from '@mui/icons-material/SignalWifi2Bar';
import SignalWifi3Bar from '@mui/icons-material/SignalWifi3Bar';
import Speed from '@mui/icons-material/Speed';
import Stop from '@mui/icons-material/Stop';
import StopScreenShare from '@mui/icons-material/StopScreenShare';
import Subtitles from '@mui/icons-material/Subtitles';
import Timeline from '@mui/icons-material/Timeline';
import Videocam from '@mui/icons-material/Videocam';
import VideocamOff from '@mui/icons-material/VideocamOff';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import VolumeOff from '@mui/icons-material/VolumeOff';
import VolumeUp from '@mui/icons-material/VolumeUp';
import WarningAmber from '@mui/icons-material/WarningAmber';
// Adapted MUI icons matching Jitsi's icon naming conventions
export const IconAddUser_MUI = adapt(PersonAdd);
export const IconArrowBack_MUI = adapt(ArrowBack);
export const IconArrowDown_MUI = adapt(ArrowDropDown);
export const IconArrowDownLarge_MUI = adapt(KeyboardArrowDown);
export const IconArrowLeft_MUI = adapt(ChevronLeft);
export const IconArrowRight_MUI = adapt(ChevronRight);
export const IconArrowUp_MUI = adapt(ArrowDropUp);
export const IconArrowUpLarge_MUI = adapt(KeyboardArrowUp);
export const IconAudioOnly_MUI = adapt(Visibility);
export const IconAudioOnlyOff_MUI = adapt(VisibilityOff);
export const IconBell_MUI = adapt(Notifications);
export const IconBluetooth_MUI = adapt(Bluetooth);
export const IconCalendar_MUI = adapt(CalendarMonth);
export const IconCameraRefresh_MUI = adapt(Cameraswitch);
export const IconCar_MUI = adapt(DirectionsCar);
export const IconChatUnread_MUI = adapt(MarkChatUnread);
export const IconCheck_MUI = adapt(Check);
export const IconCloseCircle_MUI = adapt(Cancel);
export const IconCloseLarge_MUI = adapt(Close);
export const IconCloudUpload_MUI = adapt(CloudUpload);
export const IconCode_MUI = adapt(Code);
export const IconConnection_MUI = adapt(SignalCellularAlt);
export const IconConnectionInactive_MUI = adapt(SignalCellularConnectedNoInternet0Bar);
export const IconCopy_MUI = adapt(ContentCopy);
export const IconDeviceHeadphone_MUI = adapt(Headphones);
export const IconDotsHorizontal_MUI = adapt(MoreHoriz);
export const IconDownload_MUI = adapt(Download);
export const IconE2EE_MUI = adapt(EnhancedEncryption);
export const IconEdit_MUI = adapt(Edit);
export const IconEnlarge_MUI = adapt(OpenInFull);
export const IconEnterFullscreen_MUI = adapt(Fullscreen);
export const IconEnvelope_MUI = adapt(Email);
export const IconExclamationSolid_MUI = adapt(Error);
export const IconExclamationTriangle_MUI = adapt(WarningAmber);
export const IconExitFullscreen_MUI = adapt(FullscreenExit);
export const IconFaceSmile_MUI = adapt(EmojiEmotions);
export const IconFavorite_MUI = adapt(FavoriteBorder);
export const IconFavoriteSolid_MUI = adapt(Favorite);
export const IconFeedback_MUI = adapt(Feedback);
export const IconGear_MUI = adapt(Settings);
export const IconHangup_MUI = adapt(CallEnd);
export const IconHelp_MUI = adapt(HelpOutline);
export const IconHighlight_MUI = adapt(Highlight);
export const IconImage_MUI = adapt(Image);
export const IconInfo_MUI = adapt(InfoOutlined);
export const IconInfoCircle_MUI = adapt(Info);
export const IconMessage_MUI = adapt(Chat);
export const IconMeter_MUI = adapt(Speed);
export const IconMic_MUI = adapt(Mic);
export const IconMicSlash_MUI = adapt(MicOff);
export const IconModerator_MUI = adapt(Shield);
export const IconPerformance_MUI = adapt(Timeline);
export const IconPhoneRinging_MUI = adapt(PhoneInTalk);
export const IconPin_MUI = adapt(PushPinOutlined);
export const IconPinned_MUI = adapt(PushPin);
export const IconPlay_MUI = adapt(PlayArrow);
export const IconPlus_MUI = adapt(Add);
export const IconRaiseHand_MUI = adapt(PanToolAlt);
export const IconRecord_MUI = adapt(FiberManualRecord);
export const IconRecordAccount_MUI = adapt(AccountCircle);
export const IconRecordContact_MUI = adapt(ContactPage);
export const IconRemoteControlStart_MUI = adapt(ScreenShare);
export const IconRemoteControlStop_MUI = adapt(StopScreenShare);
export const IconReply_MUI = adapt(Reply);
export const IconRestore_MUI = adapt(Restore);
export const IconScreenshare_MUI = adapt(ScreenShare);
export const IconSearch_MUI = adapt(Search);
export const IconSecurityOff_MUI = adapt(NoEncryption);
export const IconSecurityOn_MUI = adapt(Lock);
export const IconSend_MUI = adapt(Send);
export const IconShare_MUI = adapt(Share);
export const IconShareDoc_MUI = adapt(Description);
export const IconShortcuts_MUI = adapt(Keyboard);
export const IconSites_MUI = adapt(Language);
export const IconStop_MUI = adapt(Stop);
export const IconSubtitles_MUI = adapt(Subtitles);
export const IconTileView_MUI = adapt(GridView);
export const IconTrash_MUI = adapt(Delete);
export const IconUser_MUI = adapt(Person);
export const IconUserDeleted_MUI = adapt(PersonOff);
export const IconUsers_MUI = adapt(People);
export const IconVideo_MUI = adapt(Videocam);
export const IconVideoOff_MUI = adapt(VideocamOff);
export const IconVolumeOff_MUI = adapt(VolumeOff);
export const IconVolumeUp_MUI = adapt(VolumeUp);
export const IconWarning_MUI = adapt(WarningAmber);
export const IconWarningCircle_MUI = adapt(ErrorOutline);
export const IconWhiteboard_MUI = adapt(Draw);
export const IconWifi1Bar_MUI = adapt(SignalWifi1Bar);
export const IconWifi2Bars_MUI = adapt(SignalWifi2Bar);
export const IconWifi3Bars_MUI = adapt(SignalWifi3Bar);
